import {
  EquipmentType,
  GLCode,
  Mode,
  Money,
  UserRef,
  ZonedDateTimeRange,
} from '@rygen/rygen-ui/model';

import { ClientSummaryDto } from '../ClientSummaryDto';
import { EmbeddableLocation } from '../EmbeddableLocation';
import { ReferenceNumber } from '../ReferenceNumber';
import { ShipmentStopType } from '../shipment/ShipmentStopType';

import { OrderShippingPoint } from './OrderShippingPoint';

export class OrderSearchData {
  id?: number;
  originLocationName?: string;
  originLocality?: string;
  originRegion?: string;
  originPostalCode?: string;
  originLocationGroupIds?: string[];
  destinationLocationName?: string;
  destinationLocality?: string;
  destinationRegion?: string;
  destinationPostalCode?: string;
  destinationLocationGroupIds?: string[];
  client?: ClientSummaryDto;
  direction?: string;
  equipmentType?: EquipmentType;
  status?: string;
  requestedPickupDateRange?: ZonedDateTimeRange;
  pickupAppointmentRequired?: boolean = false;
  requestedDeliveryDateRange?: ZonedDateTimeRange;
  deliveryAppointmentRequired?: boolean = false;
  referenceNumbers?: ReferenceNumber[];
  lineItemReferenceNumbers?: ReferenceNumber[];
  glCodes?: GLCode[];
  tags?: string[];
  weight?: number;
  weightUom?: string;
  handlingUnitCount?: number;
  handlingUnitDescription?: string;
  productNumber?: string;
  productDescription?: string;
  hazmat: boolean = false;
  freightAllowanceEnabled: boolean = false;
  freightAllowance?: Money;
  spotMarketRequestCount: number = 0;
  spotMarketAcceptedCount: number = 0;
  assignedUsers?: UserRef[];
  cancellationReason?: string;
  requestedMode?: Mode;
  source?: string;
  swc?: boolean;
  customerRouted?: boolean;
  hot?: boolean;
  hasAnyUnplannedShipOrders?: boolean;
  totalMileage?: number;

  constructor(opts?: Partial<OrderSearchData>) {
    if (opts?.id != null) {
      this.id = opts.id;
    }
    if (opts?.originLocationName != null) {
      this.originLocationName = opts.originLocationName;
    }
    if (opts?.originLocality != null) {
      this.originLocality = opts.originLocality;
    }
    if (opts?.originRegion != null) {
      this.originRegion = opts.originRegion;
    }
    if (opts?.originPostalCode != null) {
      this.originPostalCode = opts.originPostalCode;
    }
    if (opts?.originLocationGroupIds != null) {
      this.originLocationGroupIds = [...opts.originLocationGroupIds];
    }
    if (opts?.destinationLocationName != null) {
      this.destinationLocationName = opts.destinationLocationName;
    }
    if (opts?.destinationLocality != null) {
      this.destinationLocality = opts.destinationLocality;
    }
    if (opts?.destinationRegion != null) {
      this.destinationRegion = opts.destinationRegion;
    }
    if (opts?.destinationPostalCode != null) {
      this.destinationPostalCode = opts.destinationPostalCode;
    }
    if (opts?.destinationLocationGroupIds != null) {
      this.destinationLocationGroupIds = [...opts.destinationLocationGroupIds];
    }
    if (opts?.client != null) {
      this.client = new ClientSummaryDto(opts.client);
    }
    if (opts?.direction != null) {
      this.direction = opts.direction;
    }
    if (opts?.equipmentType != null) {
      this.equipmentType = new EquipmentType(opts.equipmentType);
    }
    if (opts?.status != null) {
      this.status = opts.status;
    }
    if (opts?.requestedPickupDateRange != null) {
      this.requestedPickupDateRange = new ZonedDateTimeRange(
        opts.requestedPickupDateRange,
      );
    }
    if (opts?.pickupAppointmentRequired != null) {
      this.pickupAppointmentRequired = opts.pickupAppointmentRequired;
    }
    if (opts?.requestedDeliveryDateRange != null) {
      this.requestedDeliveryDateRange = new ZonedDateTimeRange(
        opts.requestedDeliveryDateRange,
      );
    }
    if (opts?.deliveryAppointmentRequired != null) {
      this.deliveryAppointmentRequired = opts.deliveryAppointmentRequired;
    }
    if (opts?.referenceNumbers != null) {
      this.referenceNumbers = opts.referenceNumbers.map(
        (r) => new ReferenceNumber(r),
      );
    }
    if (opts?.lineItemReferenceNumbers != null) {
      this.lineItemReferenceNumbers = opts.lineItemReferenceNumbers.map(
        (r) => new ReferenceNumber(r),
      );
    }
    if (opts?.glCodes != null) {
      this.glCodes = opts.glCodes.map((g) => new GLCode(g));
    }
    if (opts?.tags != null) {
      this.tags = opts.tags;
    }
    if (opts?.weight != null) {
      this.weight = opts.weight;
    }
    if (opts?.weightUom != null) {
      this.weightUom = opts.weightUom;
    }
    if (opts?.handlingUnitCount != null) {
      this.handlingUnitCount = opts.handlingUnitCount;
    }
    if (opts?.handlingUnitDescription != null) {
      this.handlingUnitDescription = opts.handlingUnitDescription;
    }
    if (opts?.productNumber != null) {
      this.productNumber = opts.productNumber;
    }
    if (opts?.productDescription != null) {
      this.productDescription = opts.productDescription;
    }
    if (opts?.hazmat != null) {
      this.hazmat = opts.hazmat;
    }
    if (opts?.freightAllowanceEnabled != null) {
      this.freightAllowanceEnabled = opts.freightAllowanceEnabled;
    }
    if (opts?.freightAllowance) {
      this.freightAllowance = new Money(opts.freightAllowance);
    }
    if (opts?.spotMarketRequestCount) {
      this.spotMarketRequestCount = opts.spotMarketRequestCount;
    }
    if (opts?.spotMarketAcceptedCount) {
      this.spotMarketAcceptedCount = opts.spotMarketAcceptedCount;
    }
    if (opts?.assignedUsers != null) {
      this.assignedUsers = opts.assignedUsers.map((val) => new UserRef(val));
    }
    if (opts?.cancellationReason != null) {
      this.cancellationReason = opts.cancellationReason;
    }
    if (opts?.requestedMode != null) {
      this.requestedMode = opts.requestedMode;
    }
    if (opts?.source != null) {
      this.source = opts.source;
    }
    if (opts?.swc != null) {
      this.swc = opts.swc;
    }
    if (opts?.customerRouted != null) {
      this.customerRouted = opts.customerRouted;
    }
    if (opts?.hot != null) {
      this.hot = opts.hot;
    }
    if (opts?.hasAnyUnplannedShipOrders != null) {
      this.hasAnyUnplannedShipOrders = opts.hasAnyUnplannedShipOrders;
    }
    if (opts?.totalMileage != null) {
      this.totalMileage = opts.totalMileage;
    }
  }

  get owner() {
    return this.client;
  }

  get origin() {
    return new OrderShippingPoint({
      stopType: ShipmentStopType.PICKUP,
      location: new EmbeddableLocation({
        locationName: this.originLocationName,
        city: this.originLocality,
        state: this.originRegion,
        postalCode: this.originPostalCode,
      }),
    });
  }

  get destination() {
    return new OrderShippingPoint({
      stopType: ShipmentStopType.DROP_OFF,
      location: new EmbeddableLocation({
        locationName: this.destinationLocationName,
        city: this.destinationLocality,
        state: this.destinationRegion,
        postalCode: this.destinationPostalCode,
      }),
    });
  }

  get stops(): [origin: OrderShippingPoint, destination: OrderShippingPoint] {
    return [this.origin, this.destination];
  }
}
